// packages/core/src/services/SSEClientWithAuth.js

import { getAccessToken } from '../utils/tokenStorage';
import { prepareLatexForMarkdown } from '../utils/textProcessing';
import config from '../config';

class SSEClientWithAuth {
    constructor(baseUrl = null, sseStateHandlers = {}) {
        this.baseUrl = baseUrl || config.API_BASE_URL;
        this.eventSource = null;
        this.sessionId = null;
        this.messageHandlers = [];
        this.thinkingHandlers = [];
        this.resourceHandlers = [];
        this.resourceSyncHandlers = [];
        this.connectionHandlers = [];
        this.appEventHandlers = [];
        this.commandHandlers = [];
        this.errorHandlers = [];
        this.shutdownSignalHandlers = [];
        this.isConnected = false;
        this.connectionId = null;
        this.user = null;
        this.getLastSessionMessageId = sseStateHandlers.getLastId || (() => 0);
        this.setLastSessionMessageId = sseStateHandlers.setLastId || (() => {});
    }

    async connect(sessionId, user) {
        if (this.sessionId === sessionId && this.isConnected && this.eventSource) {
            console.log(`Already connected to session ${sessionId}, reusing connection`);
            return Promise.resolve();
        }
        if (this.sessionId && this.sessionId !== sessionId) {
            this.disconnect();
        }
        if (!sessionId || typeof sessionId !== 'string') {
            console.error('Invalid session ID:', sessionId);
            throw new Error('Session ID must be a string');
        }
        this.sessionId = sessionId;
        this.user = user;
        this.connectionId = Date.now().toString(36) + Math.random().toString(36).substr(2);
        try {
            const token = getAccessToken();
            if (!token) {
                throw new Error("Authentication required for event stream");
            }
            const url = new URL(`${this.baseUrl}/api/v1/sse/events/${sessionId}`);
            url.searchParams.append('auth_token', token);
            const lastId = this.getLastSessionMessageId();
            if (lastId) {
                console.warn(`[SSEClient] Reconnecting for session ${sessionId}. Resuming from last event ID: ${lastId}`);
                url.searchParams.append('last_event_id', lastId);
            }
            url.searchParams.append('_t', Date.now());
            url.searchParams.append('cid', this.connectionId);
            const sseUrl = url.toString();

            return new Promise((resolve, reject) => {
                console.log(`[SSEClient] Attempting to connect to: ${sseUrl}`);
                this.eventSource = new EventSource(sseUrl, { withCredentials: true });

                const openHandler = () => {
                    console.log(`[SSEClient] Connection opened successfully for session ${sessionId}.`);
                    this.isConnected = true;
                    this._notifyConnectionHandlers({ connected: true });
                    this.eventSource.removeEventListener('error', initialErrorHandler);
                    this.setupMessageHandlers();
                    resolve();
                };

                const initialErrorHandler = (error) => {
                    console.error(`[SSEClient] Initial connection error for session ${sessionId}. ReadyState: ${this.eventSource?.readyState}`, error);
                    
                    if (this.eventSource?.readyState === EventSource.CLOSED && !this.isConnected) {
                         this.eventSource.removeEventListener('open', openHandler);
                         this.eventSource.removeEventListener('error', initialErrorHandler);
                         this.isConnected = false;
                         this._notifyConnectionHandlers({ connected: false, error: new Error("Failed to connect to event stream.") });
                         reject(new Error("SSE connection failed and will not retry."));
                    }
                };

                this.eventSource.addEventListener('open', openHandler, { once: true });
                this.eventSource.addEventListener('error', initialErrorHandler);
            });

        } catch (error) {
            console.error(`Error setting up SSE connection for session ${sessionId}:`, error);
            this.isConnected = false;
            this._notifyConnectionHandlers({ connected: false, error });
            throw error;
        }
    }

    setupMessageHandlers() {
        if (!this.eventSource) return;

        // Handle browser-level auto-reconnections. When the browser's built-in
        // EventSource retry succeeds, this fires and re-notifies React state.
        this.eventSource.onopen = () => {
            console.log(`[SSEClient] Connection (re)opened for session ${this.sessionId}`);
            this.isConnected = true;
            this._notifyConnectionHandlers({ connected: true });
        };

        this.eventSource.onerror = (error) => {
            console.error(`SSE connection error for session ${this.sessionId}. ReadyState: ${this.eventSource?.readyState}`, error);
            this.isConnected = false;
            this._notifyConnectionHandlers({ connected: false, error: new Error("Connection to server lost.") });
        };

        this.eventSource.addEventListener('message', (event) => {
            console.log('%c[SSE RAW EVENT]', 'color: orange; font-weight: bold;', event.data);
            let data;
            try {
                if (event.lastEventId) {
                    const currentId = parseInt(event.lastEventId, 10);
                    if (!isNaN(currentId)) {
                        const lastId = this.getLastSessionMessageId();
                        if (currentId > lastId + 1) {
                            console.error(`[SSE Order Check] Missing message(s). Expected ID ${lastId + 1}, but received ${currentId}.`);
                        } else if (currentId <= lastId) {
                            console.warn(`[SSE Order Check] Received old or duplicate message. Last seen ID: ${lastId}, Received ID: ${currentId}.`);
                        }
                        this.setLastSessionMessageId(currentId);
                    }
                }
                data = JSON.parse(event.data);
                const eventName = data.msg_type || data.type || 'unknown';
                const eventId = event.lastEventId || 'N/A';
                console.warn(`[SSE RECEIVED] Event: '${eventName}', ID: ${eventId}, Session: ${this.sessionId}`);
                if (data && typeof data.content === 'string') {
                    data.content = prepareLatexForMarkdown(data.content);
                }
                if (data) {
                    if (!data.timestamp) data.timestamp = new Date().toISOString();
                    const msgType = data.msg_type || data.type;
                    const source = data.source;
                    const to = data.to;
                    if (msgType === 'THINKING') {
                        this._notifyThinkingHandlers({ ...data, type: 'thinking' });
                    } else if (msgType === 'APP_EVENT') {
                        this._notifyAppEventHandlers({ ...data, type: 'app_event' });
                    } else if (msgType === 'INSTANCE_SHUTDOWN_IMMINENT') {
                        this._notifyShutdownSignalHandlers({ ...data, type: 'shutdown_signal' });
                    } else if (msgType === 'RESOURCE_SYNC') {
                        this._notifyResourceSyncHandlers({ ...data, type: 'resource_sync' });
                    } else if (msgType === 'RESOURCE' || data.type === 'resource_update_signal') {
                        this._notifyResourceHandlers({ ...data, type: 'resource' });
                    } else if (msgType === 'ERROR') {
                        this._notifyErrorHandlers({ ...data, type: 'error' });
                    } else if (to === 'User' ||
                               (this.user && this.user.preferred_name && to === this.user.preferred_name) ||
                               source === 'User' || msgType === 'USER_INPUT') {
                        this._notifyMessageHandlers({ ...data, type: 'message' });
                    } else if (msgType === 'MESSAGE') {
                        this._notifyCommandHandlers({ ...data, type: 'agent_message' });
                    } else {
                        this._notifyCommandHandlers({ ...data, type: 'internal_command' });
                    }
                }
            } catch (err) {
                console.error('Error processing SSE message:', err, 'Raw data:', event.data);
            }
        });

        this.eventSource.addEventListener('ping', () => {
            console.debug('SSE ping received');
        });
    }

    disconnect() {
        if (this.eventSource) {
            console.log(`Closing SSE connection for session ${this.sessionId}`);
            this.eventSource.close();
            this.eventSource = null;
            this.isConnected = false;
            this._notifyConnectionHandlers({ connected: false });
        }
    }

    onMessage(handler) { this.messageHandlers.push(handler); return () => { this.messageHandlers = this.messageHandlers.filter(h => h !== handler); }; }
    onThinking(handler) { this.thinkingHandlers.push(handler); return () => { this.thinkingHandlers = this.thinkingHandlers.filter(h => h !== handler); }; }
    onResource(handler) { this.resourceHandlers.push(handler); return () => { this.resourceHandlers = this.resourceHandlers.filter(h => h !== handler); }; }
    onResourceSync(handler) { this.resourceSyncHandlers.push(handler); return () => { this.resourceSyncHandlers = this.resourceSyncHandlers.filter(h => h !== handler); }; }
    onAppEvent(handler) { this.appEventHandlers.push(handler); return () => { this.appEventHandlers = this.appEventHandlers.filter(h => h !== handler); }; }
    onCommand(handler) { this.commandHandlers.push(handler); return () => { this.commandHandlers = this.commandHandlers.filter(h => h !== handler); }; }
    onConnectionChange(handler) { this.connectionHandlers.push(handler); return () => { this.connectionHandlers = this.connectionHandlers.filter(h => h !== handler); }; }
    onError(handler) { this.errorHandlers.push(handler); return () => { this.errorHandlers = this.errorHandlers.filter(h => h !== handler); }; }
    onShutdownSignal(handler) { this.shutdownSignalHandlers.push(handler); return () => { this.shutdownSignalHandlers = this.shutdownSignalHandlers.filter(h => h !== handler); }; }

    _notifyMessageHandlers(message) { for (const handler of this.messageHandlers) { try { handler(message); } catch (err) { console.error('Error in message handler:', err); } } }
    _notifyThinkingHandlers(thinking) { for (const handler of this.thinkingHandlers) { try { handler(thinking); } catch (err) { console.error('Error in thinking handler:', err); } } }
    _notifyResourceHandlers(resource) { for (const handler of this.resourceHandlers) { try { handler(resource); } catch (err) { console.error('Error in resource handler:', err); } } }
    _notifyResourceSyncHandlers(syncEvent) { for (const handler of this.resourceSyncHandlers) { try { handler(syncEvent); } catch (err) { console.error('Error in resource sync handler:', err); } } }
    _notifyAppEventHandlers(event) { for (const handler of this.appEventHandlers) { try { handler(event); } catch (err) { console.error('Error in app_event handler:', err); } } }
    _notifyCommandHandlers(command) { for (const handler of this.commandHandlers) { try { handler(command); } catch (err) { console.error('Error in command handler:', err); } } }
    _notifyConnectionHandlers(state) { for (const handler of this.connectionHandlers) { try { handler(state); } catch (err) { console.error('Error in connection handler:', err); } } }
    _notifyErrorHandlers(error) { for (const handler of this.errorHandlers) { try { handler(error); } catch (err) { console.error('Error in error handler:', err); } } }
    _notifyShutdownSignalHandlers(signal) { for (const handler of this.shutdownSignalHandlers) { try { handler(signal); } catch (err) { console.error('Error in shutdown signal handler:', err); } } }
}

export default SSEClientWithAuth;